import { Avata } from "@/components/common/Avata";
import { ROUTES } from "@/constants/routes";
import HomeIcon from "@public/svgs/home-icon.svg";
import PolliaIcon from "@public/svgs/pollia-icon.svg";
import PolliaWordmark from "@public/svgs/pollia-wordmark.svg";
import { Typo } from "@repo/ui/components";
import { ChevronLeftIcon, SearchIcon } from "lucide-react";
import Link from "next/link";
import type { ReactNode } from "react";

export interface SiteHeaderViewProps {
  showBack?: boolean;
  showHomeIcon?: boolean;
  title?: string;
  showSearch?: boolean;
  showProfile?: boolean;
  fallbackRight?: ReactNode;
  rightExtra?: ReactNode;
  user?: { name: string } | null;
  profileImageUrl?: string | null;
  onBack?: () => void;
  onSearchClick?: () => void;
  onProfileClick?: () => void;
}

function BackButton({ onBack }: { onBack?: () => void }) {
  return (
    <button type="button" onClick={onBack} className="flex size-12 items-center justify-center">
      <ChevronLeftIcon className="size-6" />
    </button>
  );
}

function HomeButton() {
  return (
    <Link href={ROUTES.HOME} className="flex size-12 items-center justify-center">
      <HomeIcon className="size-6" />
    </Link>
  );
}

function LogoLink() {
  return (
    <Link href={ROUTES.HOME} className="flex shrink-0 items-center gap-[2.775px] p-3">
      <PolliaIcon className="size-4 text-primary" />
      <PolliaWordmark className="h-[22px] text-black" />
    </Link>
  );
}

function HeaderLeft({
  title,
  showBack,
  showHomeIcon,
  onBack,
}: Pick<SiteHeaderViewProps, "title" | "showBack" | "showHomeIcon" | "onBack">) {
  if (title) {
    return (
      <div className="flex items-center">
        {showBack && <BackButton onBack={onBack} />}
        <Typo.SubTitle className="text-base">{title}</Typo.SubTitle>
      </div>
    );
  }
  if (showBack) return <BackButton onBack={onBack} />;
  if (showHomeIcon) return <HomeButton />;
  return <LogoLink />;
}

function HeaderRight({
  title,
  showSearch,
  showProfile,
  fallbackRight,
  rightExtra,
  user,
  profileImageUrl,
  onSearchClick,
  onProfileClick,
}: Pick<
  SiteHeaderViewProps,
  | "title"
  | "showSearch"
  | "showProfile"
  | "fallbackRight"
  | "rightExtra"
  | "user"
  | "profileImageUrl"
  | "onSearchClick"
  | "onProfileClick"
>) {
  const hasTitle = !!title;

  return (
    <div className="flex items-center gap-0">
      {!hasTitle && showSearch && (
        <button
          type="button"
          onClick={onSearchClick}
          className="flex shrink-0 items-center justify-center size-12"
        >
          <SearchIcon className="size-6" />
        </button>
      )}
      {!hasTitle &&
        showProfile &&
        (user ? (
          <button
            onClick={onProfileClick}
            type="button"
            className="flex shrink-0 items-center justify-center size-12"
          >
            <Avata size="small" imageUrl={profileImageUrl} />
          </button>
        ) : (
          <div className="shrink-0">{fallbackRight}</div>
        ))}
      {rightExtra}
    </div>
  );
}

export function SiteHeaderView({
  showBack = false,
  showHomeIcon = false,
  title,
  showSearch = true,
  showProfile = true,
  fallbackRight,
  rightExtra,
  user,
  profileImageUrl,
  onBack,
  onSearchClick,
  onProfileClick,
}: SiteHeaderViewProps) {
  return (
    <header className="sticky top-0 z-50 flex h-[56px] items-center justify-between bg-white px-1">
      <HeaderLeft title={title} showBack={showBack} showHomeIcon={showHomeIcon} onBack={onBack} />
      <HeaderRight
        title={title}
        showSearch={showSearch}
        showProfile={showProfile}
        fallbackRight={fallbackRight}
        rightExtra={rightExtra}
        user={user}
        profileImageUrl={profileImageUrl}
        onSearchClick={onSearchClick}
        onProfileClick={onProfileClick}
      />
    </header>
  );
}
